// src/components/ResumeUpload.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import api, { getCsrfToken } from '../api/client';
import { AuthContext } from '../App';
import { FiEye } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import PremiumSelect from './admin/component/PremiumSelect';

export default function ResumeUpload({ onSelect, adminToken, onLogout }) {
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [resumes, setResumes] = useState([]);

  // const handleLogout = () => {
  //   if (onLogout) {
  //     onLogout();
  //   } else {
  //     // Handle regular logout if needed
  //   }
  // };
  const [selectedResume, setSelectedResume] = useState(null);
  const [error, setError] = useState('');
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    accent: 'us'
  });
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadResumes = useCallback(async () => {
  setLoading(true);
  setError('');
  try {
    console.log('Loading resumes with token:', token ? 'present' : 'missing');
    const response = await api.get('resumes/');
    console.log('Resumes loaded:', response.data);

    if (response.data && Array.isArray(response.data.results)) {
      const resumeList = response.data.results;
      console.log('Available resume IDs:', resumeList.map(r => r.id));

      const processedList = resumeList.map(resume => {
        if (resume.file && !resume.file.startsWith('http')) {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          resume.file = `${baseUrl}${resume.file}`;
        }
        return resume;
      });

      setResumes(processedList);
    } else {
      console.error('Unexpected response format:', response.data);
      setError('Failed to load resumes: Invalid response format');
      setResumes([]);
    }
  } catch (error) {
    console.error('Failed to load resumes:', error);
    if (error.response?.status === 401) {
      setError('Please log in to view your resumes.');
    } else {
      setError('Failed to load resumes. Please try again.');
    }
    setResumes([]);
  } finally {
    setLoading(false);
  }
}, [token]);


  // Reload resumes when token changes and fetch CSRF token
  useEffect(() => {
    const initComponent = async () => {
      // Always fetch CSRF token on component mount
      try {
        await api.get('/');
        console.log('CSRF token fetched on component mount');
      } catch (err) {
        console.warn('Failed to fetch CSRF token on mount:', err);
      }

      if (token) {
        loadResumes();
      } else {
        setResumes([]);
        setSelectedResume(null);
        setLoading(false);
      }
    };

    initComponent();
  }, [token, loadResumes]);

  const handleResumeSelect = async (resume) => {
    console.log('Selected resume:', resume);
    try {
      // Verify the resume exists and get fresh data
      const response = await api.get(`resumes/${resume.id}/`);
      console.log('Resume verification before selection:', response.data);

      if (!response.data || !response.data.id) {
        throw new Error('Invalid resume data received');
      }

      // Store the verified resume data
      setSelectedResume(response.data);
      setShowPreferences(true);
    } catch (error) {
      console.error('Failed to verify resume:', error);
      setError('Failed to select resume. Please try again.');
      loadResumes();
    }
  };

  const handleDeleteResume = async (resumeId) => {
    const confirmMsg = t('candidate.archiveResumeMessage', 'Are you sure you want to archive this resume?');
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`resumes/${resumeId}/`);
      setResumes(prevResumes => prevResumes.filter(r => r.id !== resumeId));
    } catch (error) {
      console.error('Failed to archive resume:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to archive this resume.');
      } else {
        setError('Failed to arhive resume. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleStartInterview = async () => {
    if (!selectedResume) {
      setError('No resume selected');
      return;
    }

    try {
      console.log('Starting interview with resume:', selectedResume);
      console.log('Current preferences:', preferences);

      // Check user credits first
      const creditResponse = await api.get('observability/user-credits/');
      const credits = creditResponse.data.remaining_credits || 0;
      
      if (credits <= 0) {
        setError(t('resumeUpload.resumeManagement.noCredits'));
        return;
      }

      // Double check the resume ID
      if (!selectedResume.id) {
        throw new Error('Invalid resume ID in selected resume');
      }

      // Log available resumes
      console.log('Available resumes:', resumes.map(r => ({ id: r.id, name: r.file })));

      // Verify the resume one more time before creating session
      console.log('Verifying resume ID:', selectedResume.id);
      const resumeCheck = await api.get(`resumes/${selectedResume.id}/`);
      console.log('Final resume verification:', resumeCheck.data);

      if (!resumeCheck.data || !resumeCheck.data.id) {
        throw new Error('Resume verification failed');
      }

      // Use the verified ID from the server
      const verifiedResumeId = resumeCheck.data.id;
      console.log('Using verified resume ID:', verifiedResumeId);

      const requestData = {
        resume_id: verifiedResumeId
      };

      console.log('Sending request data:', requestData);

      const response = await api.post('sessions/', requestData);
      console.log('Session created:', response.data);
      onSelect(response.data.id);
    } catch (error) {
      console.error('Failed to start interview:', error);
      if (error.response?.data) {
        console.error('Error details:', error.response.data);
        setError(`Failed to start interview: ${JSON.stringify(error.response.data)}`);
      } else if (error.response?.status === 403) {
        setError('You do not have permission to use this resume.');
      } else {
        setError('Failed to start interview. Please try again.');
      }
      // Reload resumes to ensure we have the latest data
      await loadResumes();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');

    // Ensure we have a CSRF token before upload
    try {
      // Make a GET request to the API root to set CSRF cookie
      await api.get('/');
      console.log('CSRF token refreshed before upload');
    } catch (err) {
      console.warn('Failed to fetch CSRF token before upload:', err);
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      console.log('Uploading resume:', file.name);
      const response = await api.post('resumes/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': getCsrfToken(), // Explicitly add CSRF token for FormData
        },
      });
      console.log('Resume upload response:', response.data);

      if (!response.data.id) {
        throw new Error('No resume ID received from server');
      }

      // Reload the entire resume list after upload
      await loadResumes();
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Failed to upload resume:', error);
      if (error.response?.data?.detail?.includes('CSRF')) {
        setError('CSRF validation failed. Please refresh the page and try again.');
      } else if (error.response?.data) {
        console.error('Upload error details:', error.response.data);
        setError(`Failed to upload resume: ${JSON.stringify(error.response.data)}`);
      } else if (error.response?.status === 401) {
        setError('Please log in to upload a resume.');
      } else {
        setError('Failed to upload resume. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  // Update testVoice to only use accent
  const testVoice = () => {
    if (!('speechSynthesis' in window)) {
      alert('Speech synthesis not supported in this browser.');
      return;
    }
    const synth = window.speechSynthesis;
    const voices = synth.getVoices();
    const { accent } = preferences;
    let accentVoices = voices.filter(v => {
      if (accent === 'us') return v.lang.includes('en-US');
      if (accent === 'uk') return v.lang.includes('en-GB');
      if (accent === 'india') return v.lang.includes('en-IN');
      return v.lang.startsWith('en');
    });
    let preferredVoice = accentVoices.length > 0 ? accentVoices[0] : null;
    const utterance = new window.SpeechSynthesisUtterance('This is a sample voice for your interview preferences.');
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    synth.speak(utterance);
  };

  if (showPreferences) {
    return (
      <>
        <div className="container">
          <div className="card">
            <h2>Interview Preferences</h2>
            <div className="preferences-form">
              <div className="preference-group">
                <label>Accent</label>
                <PremiumSelect
                  variant="b2c"
                  value={preferences.accent}
                  onChange={(val) => setPreferences({ accent: val })}
                  options={[
                    { value: 'us', label: 'US English' },
                    { value: 'uk', label: 'UK English' },
                    { value: 'india', label: 'Indian English' },
                  ]}
                  searchable={false}
                />
              </div>
              <div className="button-group">
                <button
                  className="button"
                  onClick={() => setShowPreferences(false)}
                >
                  Back
                </button>
                <button
                  className="button"
                  onClick={handleStartInterview}
                >
                  Start Interview
                </button>
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={testVoice}
                  style={{ marginLeft: 8 }}
                >
                  Test Voice
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading your resumes...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container">
        <h2>Your Resumes</h2>
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}
        <div className="card">
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            className="input"
            onChange={handleFileUpload}
            disabled={uploading}
          />
          {uploading && <p>Uploading resume...</p>}
        </div>
        <div>
          {resumes.length === 0 ? (
            <div className="alert alert-info">
              No resumes uploaded yet. Please upload your resume to start an interview.
            </div>
          ) : (
            resumes.map((resume) => (
              <div
                key={resume.id}
                className="resume-card"
              >
                <div className="resume-info">
                  <h3>{resume.file.split('/').pop()}</h3>
                  <p>
                    Uploaded on {new Date(resume.uploaded_at).toLocaleDateString()}
                  </p>
                  {resume.file.toLowerCase().endsWith('.pdf') && (
                    <div className="pdf-preview">
                      <object
                        data={`${resume.file}#toolbar=0&navpanes=0`}
                        type="application/pdf"
                        width="100%"
                        height="300px"
                        style={{ border: '1px solid #ddd', borderRadius: '4px', marginTop: '10px' }}
                      >
                        <p>Your browser does not support PDF preview. <a href={resume.file} target="_blank" rel="noopener noreferrer">Click here to view</a></p>
                      </object>
                    </div>
                  )}
                </div>
                <div className="resume-actions">
                  <button
                    onClick={() => handleResumeSelect(resume)}
                    className="button"
                  >
                    Start Interview
                  </button>
                  <button
                    onClick={() => handleDeleteResume(resume.id)}
                    className="button button-secondary"
                    disabled={deleting}
                  >
                    Archive
                  </button>
                  <a
                    href={resume.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button"
                    style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                  >
                    <FiEye /> View PDF
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
